import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Eye } from "lucide-react";
import api from "../Axios/api";
import Loader from "../common/Loader";
const DetailsScreen = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  const state = location.state;
  const fetchData = async () => {
    try {
      setIsLoading(true);
      const res = await api.post(`/summary-report/get-details`, {
        indentId: state.indentId,
        uniqueId: state.uniqueId,
        po_num: state.po_number,
      });
      setData(res.data);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  if (isLoading) {
    return <Loader />;
  }

  const indent = data.indentData || {};
  const poList = data.poData || [];
  const receiptList = data.receiptData.receipt || [];

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-semibold">Summary Details</h1>
        <button
          className="px-4 py-2 bg-purple-200 text-purple-700 rounded hover:bg-purple-300"
          onClick={() => navigate(-1)}
        >
          Back
        </button>
      </div>

      {/* Indent Details */}
      <div className="bg-white shadow rounded-lg p-4">
        <h2 className="text-lg font-semibold mb-3 border-b pb-2">
          Indent Details
        </h2>
        <div className="grid grid-cols-3 gap-4 text-sm">
          <p>
            <strong>Indent Factory:</strong> {indent.branch || "N/A"}
          </p>
          <p>
            <strong>Indent Date:</strong>{" "}
            {new Date(indent.indent_date).toLocaleDateString("en-GB") || "N/A"}
          </p>
          <p>
            <strong>Indent By:</strong> {indent.indent_by || "N/A"}
          </p>
          <p>
            <strong>Indent No.:</strong> {indent.indent_number || "N/A"}
          </p>
          <p>
            <strong>Indent Remark:</strong> {indent.indent_remark || "N/A"}
          </p>
          <p>
            <strong>Indent Status:</strong> {state.status || "N/A"}
          </p>
        </div>
      </div>

      {/* PO Details */}
      <div className="bg-white shadow rounded-lg p-4">
        <h2 className="text-lg font-semibold mb-3 border-b pb-2">PO Details</h2>
        <h3 className="text-sm font-semibold mb-3 text-blue-600">
          <span className="text-gray-700">Party Name : </span> {state.party}
        </h3>

        <table className="w-full border text-sm">
          <thead className="bg-gray-100">
            <tr>
              <th className="border px-2 py-1">Sno</th>
              <th className="border px-2 py-1">Date</th>
              <th className="border px-2 py-1">PO No.</th>
              <th className="border px-2 py-1">Item</th>
              <th className="border px-2 py-1">Req. Qty</th>
              <th className="border px-2 py-1">Balance Qty</th>
              <th className="border px-2 py-1">View</th>
            </tr>
          </thead>
          <tbody>
            {poList.length > 0 ? (
              poList.map((po, i) => {
                const receiptQty = Number(data.receiptData?.globalTotalQty || 0);

                const balanceQty =
                  Number(po.required_qty || 0) - receiptQty;
                return (
                  <tr key={i}>
                    <td className="border px-2 py-1 text-center">{i + 1}</td>
                    <td className="border px-2 py-1">
                      {" "}
                      {new Date(po.po_date).toLocaleDateString("en-GB")}
                    </td>
                    <td className="border px-2 py-1">{po.po_num}</td>
                    <td className="border px-2 py-1">{po.item_title}</td>
                    <td className="border px-2 py-1 text-center">
                      {po.required_qty}
                    </td>
                    <td className="border px-2 py-1 text-center">
                      {balanceQty.toFixed(2)}
                    </td>
                    <td className="border px-2 py-1 text-center">
                      <button
                        className="text-blue-500 underline hover:text-blue-700 mr-5"
                        onClick={() =>
                          navigate(`/quote-comparison-detail/${state.uniqueId}`)
                        }
                      >
                        {" "}
                        <Eye size={20} />
                      </button>
                    </td>
                  </tr>)
              })
            ) : (
              <tr>
                <td colSpan="6" className="text-center py-3 text-gray-500">
                  No PO data available
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Receipt Details */}
      <div className="bg-white shadow rounded-lg p-4">
        <h2 className="text-lg font-semibold mb-3 border-b pb-2">
          Receipt Details
        </h2>

        <table className="w-full border text-sm">
          <thead className="bg-gray-100">
            <tr>
              <th className="border px-2 py-1">Sno</th>
              <th className="border px-2 py-1">Receipt No.</th>
              <th className="border px-2 py-1">Date</th>
              <th className="border px-2 py-1">Item</th>
              <th className="border px-2 py-1">View</th>
            </tr>
          </thead>
          <tbody>
            {receiptList.length > 0 ? (
              [...receiptList]
                .sort((a, b) => new Date(b.entry_date) - new Date(a.entry_date))
                .map((rec, i) => (
                  <tr key={i}>
                    <td className="border px-2 py-1 text-center">{i + 1}</td>
                    <td className="border px-2 py-1">{rec.receipt_no}</td>
                    <td className="border px-2 py-1">
                      {new Date(rec.entry_date).toLocaleDateString("en-GB")}
                    </td>
                    <td className="border px-2 py-1">
                      {rec.receipt_item_info?.[0]?.receipt_item_name || "N/A"}
                    </td>
                    <td className="border px-2 py-1 text-center">
                      <button
                        className="text-blue-500 hover:text-blue-700"
                        onClick={() => {
                          const receiptId = rec._id;
                          localStorage.setItem("receiptId", receiptId);
                          window.open("/create-receipt-form", "_blank");
                        }}
                      >
                        <Eye size={18} />
                      </button>
                    </td>
                  </tr>
                ))
            ) : (
              <tr>
                <td colSpan="5" className="text-center py-3 text-gray-500">
                  No receipts available
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default DetailsScreen;
